import {ReactElement, useEffect, useMemo, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {spanToMinutes} from "../../util/CalendarUtil";
import './Appointments.css'
import {useRpcSender} from "../../RpcSender";
import {CancelBookingRequest, CancelBookingResponse, GetBookingsResponse, space} from "../../compiled";
import {RpcState} from "../../util/RpcState";
import IApiBookingProto = space.kenko.proto.IApiBookingProto;
import {ActionButton} from "../../util/UI/ActionButton";

export const Appointments = () => {

  const rpcSender = useRpcSender();
  const navigate = useNavigate();
  const [rpcState, setRpcState] = useState<RpcState>({state: 'pending'})
  const [bookings, setBookings] = useState<IApiBookingProto[]>([]);

  useEffect(() => {
    rpcSender.send("get_bookings", new Uint8Array())
        .then(bytes => {
          const response = GetBookingsResponse.decode(bytes);
          setBookings(response.booking);
          setRpcState({state: 'success'})
        })
        .catch(e => {
          setRpcState({state: 'error', message: 'Network error loading bookings.'});
        });
  }, [])
  
  const cancelBooking = (booking: IApiBookingProto) => {
    
    const className = booking.details?.class?.className || booking.details?.service?.serviceName;
    
    if (!window.confirm(`Are you sure you want to cancel ${className}?`)) {
      return;
    }

    const request = CancelBookingRequest.encode(new CancelBookingRequest({
      bookingId: booking.bookingId
    })).finish();

    rpcSender.send("cancel_booking", request)
        .then(bytes => {
          const response = CancelBookingResponse.decode(bytes);
          setBookings(bookings => bookings.filter(booking => booking.bookingId != response.deletedBookingId));
          if (!response.okay) {
            alert('Error deleting your booking.');
          }
        })
        .catch(e => {
          console.error(e);
          alert('Network error deleting your booking.');
        });
  };

  const bookingBox = (booking: IApiBookingProto, actionButton: ReactElement) => {

    const appointmentStart = new Date(booking.startTimeMs as number)
    const appointmentLength = spanToMinutes(booking.startTimeMs as number,
        booking.endTimeMs as number)

    const serviceName = booking.details?.service?.serviceName ?? booking.details?.class?.className;
    const providerName = booking.details?.service?.practitionerName ?? booking.details?.class?.locationName
    const virtualMeetingLink = booking.details?.service?.virtualMeetingLink


    return (<div className={'AppointmentsItem'}>
      <div className={'AppointmentsItemLeft'}>
        <h2>{appointmentStart.getDate()}</h2>
        <span>{appointmentStart.toLocaleDateString(undefined, {month: 'short'})}</span>
      </div>
      <div className={'AppointmentsItemCenter'}>
        <span>{appointmentStart.toLocaleTimeString(undefined, {hour: "numeric", minute: 'numeric'})}</span>
        <span><b>{serviceName}</b></span>
        <span>{providerName} • {appointmentLength}&nbsp;min</span>
        {virtualMeetingLink && <span>
          <a style={{textDecoration: 'underline'}} target={"_blank"} href={virtualMeetingLink}>(Virtual meeting link)</a>
        </span>}
      </div>
      <div className={'AppointmentsItemRight'}>
        <div>
          {actionButton}
        </div>
      </div>

    </div>);
  }


  const noAppointmentsFoundBox = (text: string) => {
    return (<div className={'AppointmentsItem'}>
      {text}
    </div>);
  }

  const nowMs = new Date().getTime();

  const upcomingBookings = useMemo(() => {
    return bookings?.filter(booking =>
        booking.startTimeMs && booking.startTimeMs >= nowMs) || [];
  }, [bookings, nowMs])

  const pastBookings = useMemo(() => {
    return bookings?.filter(booking =>
        booking.startTimeMs && booking.startTimeMs < nowMs) || []
  }, [bookings, nowMs])

  return <div className={'AppointmentsPage'}>

    <div className={'AppointmentsBox'}>
      <div className={'AppointmentsBoxHeader'}>
        UPCOMING BOOKINGS
      </div>

      <div className={'AppointmentsItemsList'}>
        {upcomingBookings.map(booking =>
            bookingBox(booking, <button onClick={() => cancelBooking(booking)}
                                        className={'ActionButton'}>Cancel</button>)
        )}

        {rpcState.state == 'pending' &&
            <div className={'AppointmentsItem AppointmentsItemNoneFound'}>Loading...</div>}

        {rpcState.state == 'error' &&
            <div className={'AppointmentsItem AppointmentsItemNoneFound ErrorText'}>{rpcState.message}</div>}

        {rpcState.state == 'success' && upcomingBookings.length == 0 &&
            <div className={'AppointmentsItem AppointmentsItemNoneFound'}>
              You do not currently have any upcoming appointments scheduled.
              <Link to={'/search'}>
                <ActionButton>Find a practitioner</ActionButton>
              </Link>
            </div>}
      </div>
    </div>

    <div className={'AppointmentsBox'}>
      <div className={'AppointmentsBoxHeader'}>
        PAST BOOKINGS
      </div>

      <div className={'AppointmentsItemsList'}>

        {rpcState.state == 'pending' &&
            <div className={'AppointmentsItem AppointmentsItemNoneFound'}>Loading...</div>}

        {pastBookings.map(booking => bookingBox(booking,
            <ActionButton onClick={() => {

              // Link for services is slightly different
              navigate(booking.details?.service ?
                  `/book?b=${booking.businessId}` +
                  `&s=${booking.details.service.serviceId}` +
                  `&p=${booking.details.service.practitionerId}` :
                  `/book?b=${booking.businessId}`);
            }}>
              Book&nbsp;again
            </ActionButton>)
        )}

        {rpcState.state == 'pending' &&
            <div className={'AppointmentsItem AppointmentsItemNoneFound'}>Loading...</div>}

        {rpcState.state == 'error' &&
            <div className={'AppointmentsItem AppointmentsItemNoneFound ErrorText'}>{rpcState.message}</div>}

        {rpcState.state == 'success' && pastBookings.length == 0 &&
            noAppointmentsFoundBox('No past appointments found in the last 30 days')}
      </div>
    </div>

  </div>
}